import styled from 'styled-components';
import Modal from '../../../components/Modal';
import TrackedButton from '../../../components/TrackedButton';
import { useAppDispatch } from '../../../hooks';
import { setShowThemeSwitchConfirmationModal } from '../../../reducers/Trellis';
import { useMemo, useState } from 'react';
import { useTheme } from 'foundations-theming-specialty/utils';
import { changeThemeSetting } from 'foundations-theming/utils/themeSwitcher';
// eslint-disable-next-line no-restricted-imports
import { isTrellisTheme } from 'foundations-theming/utils';
import getWindow from '../../../utils/getWindow';
import { useTranslation } from '../../../hooks/useNavConfig';
import useUsageTracker from '../../../hooks/useUsageTracker';
import getCurrentUrlWithoutPortalId from '../../../utils/getCurrentUrlWithoutPortalId';
import getAppName from '../../../utils/getAppName';
import { updateUserThemeSetting } from '../api/userSettingsApi';
import { Metrics } from '../../../utils/metrics';
import { LoadingSpinner } from '../../../nav-ui-components/LoadingSpinner';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ModalContent = styled.div.withConfig({
  displayName: "ThemeSwitchConfirmationModal__ModalContent",
  componentId: "sc-14an21w-0"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    width: '420px',
    minWidth: '300px',
    background: theme.color['fill-surface-default'],
    borderRadius: theme.borderRadius['200'],
    padding: theme.space['800'],
    textAlign: 'center'
  };
});
const ModalTitle = styled.h2.withConfig({
  displayName: "ThemeSwitchConfirmationModal__ModalTitle",
  componentId: "sc-14an21w-1"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return Object.assign({}, theme.typography['heading-200'], {
    color: theme.color['text-core-default'],
    marginTop: 0,
    marginBottom: theme.space['400']
  });
});
const ModalDescription = styled.p.withConfig({
  displayName: "ThemeSwitchConfirmationModal__ModalDescription",
  componentId: "sc-14an21w-2"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return Object.assign({}, theme.typography['body-100'], {
    color: theme.color['text-core-subtle'],
    marginBottom: theme.space['600']
  });
});
const ButtonContainer = styled.div.withConfig({
  displayName: "ThemeSwitchConfirmationModal__ButtonContainer",
  componentId: "sc-14an21w-3"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    display: 'flex',
    gap: theme.space['400'],
    justifyContent: 'center'
  };
});
const StyledButton = styled(TrackedButton)(({
  theme: _theme,
  $variant
}) => {
  const theme = _theme;
  const isPrimary = $variant === 'primary';
  return Object.assign({
    borderRadius: theme.borderRadius['200'],
    border: isPrimary ? 'none' : `${theme.borderWidth['100']} solid ${theme.color['border-secondary-default']}`,
    textAlign: 'center',
    transition: 'all 0.15s ease-out',
    overflow: 'hidden',
    verticalAlign: 'middle'
  }, theme.typography['body-100'], {
    padding: `${theme.space['200']} ${theme.space['600']}`,
    margin: 0,
    cursor: 'pointer',
    background: isPrimary ? theme.color['fill-primary-default'] : theme.color['fill-secondary-default'],
    color: isPrimary ? theme.color['text-primary-default'] : theme.color['text-secondary-default'],
    '&:hover': {
      background: isPrimary ? theme.color['fill-primary-hover'] : theme.color['fill-secondary-hover']
    }
  });
});
const ThemeSwitchConfirmationModal = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const {
    trackStandalone
  } = useUsageTracker();
  const [isLoading, setIsLoading] = useState(false);
  const isTrellis = useMemo(() => {
    return isTrellisTheme(theme);
  }, [theme]);
  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      if (isTrellis) {
        changeThemeSetting('canvas', 'light');
        await updateUserThemeSetting('canvas', 'light');
      } else {
        changeThemeSetting('trellis', 'light');
        await updateUserThemeSetting('trellis', 'light');
      }
      trackStandalone('Trellis Theme Switch', {
        theme: isTrellis ? 'canvas-light' : 'trellis-light',
        currentApp: getAppName(),
        currentUrl: getCurrentUrlWithoutPortalId()
      });
    } catch (error) {
      Metrics.counter('trellis-theme-switch-error', {
        theme: isTrellis ? 'canvas-light' : 'trellis-light'
      }).increment();
    }
    getWindow().location.reload();
  };
  const handleCancel = () => {
    dispatch(setShowThemeSwitchConfirmationModal(false));
  };
  return /*#__PURE__*/_jsx(Modal, {
    children: /*#__PURE__*/_jsxs(ModalContent, {
      children: [/*#__PURE__*/_jsx(ModalTitle, {
        children: t('nav.trellis.confirmationModal.title')
      }), /*#__PURE__*/_jsx(ModalDescription, {
        children: isTrellis ? t('nav.trellis.confirmationModal.toCanvasDescription') : t('nav.trellis.confirmationModal.toTrellisDescription')
      }), isLoading && /*#__PURE__*/_jsx("div", {
        style: {
          display: 'flex',
          justifyContent: 'center'
        },
        children: /*#__PURE__*/_jsx(LoadingSpinner, {})
      }), !isLoading && /*#__PURE__*/_jsxs(ButtonContainer, {
        children: [/*#__PURE__*/_jsx(StyledButton, {
          $variant: "secondary",
          onClick: handleCancel,
          children: t('nav.trellis.confirmationModal.cancel')
        }), /*#__PURE__*/_jsx(StyledButton, {
          $variant: "primary",
          onClick: () => void handleConfirm(),
          children: t('nav.trellis.confirmationModal.accept')
        })]
      })]
    })
  });
};
export default ThemeSwitchConfirmationModal;